import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { Redirect } from 'react-router-dom';
import GridContainer from 'components/Grid/GridContainer';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import ReloadPage from 'components/ReloadPage/ReloadPage';
import { DepartmentsService } from 'services/departments/departments';
import { AccountsService } from 'services/accounts/accounts';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import invalidateCache from 'utils/api/invalidateCache';
import { useDispatch } from 'react-redux';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/accountsStyle';
import { makeStyles } from '@material-ui/core';
import { translate } from 'components/Translation';
import { finalize } from 'rxjs/operators';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import SingleOrganization from './subcomponents/SingleOrganization';

// @ts-ignore
const useStyles = makeStyles(styles);

const OrganizationsList = () => {
	const classes = useStyles();
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [departments, setDepartments] = useState<SingleDepartment[]>([]);
	const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
	const [loadingDepartments, setLoadingDepartments] = useState<boolean>(false);
	const dispatch = useDispatch();
	const isMounted = useRef(true);

	const loading = loadingAccounts || loadingDepartments;
	const errorOccurred = (!accounts || accounts.length <= 0) && !loading;

	const fetchData = useCallback(() => {
		dispatch(showLoadingIndicator());
		setLoadingAccounts(true);
		AccountsService.getCorpAccountsForCurrentUser()
			.pipe(finalize(() => isMounted.current && setLoadingAccounts(false)))
			.subscribe(({ accounts }) => {
				if (isMounted.current) {
					setAccounts(accounts);
				}
			});
	}, [dispatch]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (accounts.length > 0) {
			setLoadingDepartments(true);
			dispatch(showLoadingIndicator());
			DepartmentsService.getDepartmentsForCurrentUser()
				.pipe(finalize(() => isMounted.current && setLoadingDepartments(false)))
				.subscribe(({ departments }) => {
					if (isMounted.current) {
						setDepartments(departments.filter((department) => department.deptId));
					}
				});
		}
	}, [dispatch, accounts]);

	useEffect(() => {
		if (!loadingAccounts && !loadingDepartments) {
			dispatch(hideLoadingIndicator());
		}
	}, [loadingAccounts, loadingDepartments, dispatch]);

	const reload = () => {
		invalidateCache(['getCorpAccountsForCurrentUser'], true);
		fetchData();
	};

	return (
		<GridContainer justify="flex-start">
			<BreadcrumbsItem to="/organizations">{translate('path.my.organizations')}</BreadcrumbsItem>
			<ConditionalRender
				show={!loadingAccounts && !loadingDepartments && accounts.length === 1}
				onTrue={() => <Redirect to={`organizations/${accounts[0].accountId}`} />}
			/>
			<ConditionalRender
				show={!loading}
				onTrue={() => (
					<>
						{accounts.map((account: SingleCorpAccount) => (
							<SingleOrganization key={account.accountId} account={account} departments={departments} />
						))}
					</>
				)}
			/>
			<ConditionalRender
				show={errorOccurred}
				onTrue={() => (
					<ReloadPage
						reload={reload}
						text={translate('organizations.try.again')}
						className={classes.reloadSection}
					/>
				)}
			/>
		</GridContainer>
	);
};

export default OrganizationsList;
